/** Start an offer for a customer or request. */
import { useEffect, useState, type ChangeEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type {
  CustomerResponse,
  OfferResponse,
  RequestResponse,
} from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { InputText } from "@primereact/ui/inputtext";
import { apiGet, apiPost } from "../../lib/api.js";
import { Field } from "../../components/ui/Field.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { MutationError } from "../shared.js";
import { CustomerPicker, Notice, useCustomer } from "../commercial/document.js";

/** Choose customer (or request) and title, then continue in the editor. */
export function OfferNewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestId = params.get("requestId");
  const request = useQuery({
    queryKey: ["request", requestId],
    queryFn: () =>
      apiGet<{ data: RequestResponse }>(`/requests/${requestId}`).then(
        (response) => response.data,
      ),
    enabled: Boolean(requestId),
  });
  const preselected = useCustomer(
    request.data?.customer.id ?? params.get("customerId"),
  );
  const [customer, setCustomer] = useState<CustomerResponse | null>(null);
  const [title, setTitle] = useState("");
  useEffect(() => {
    if (preselected.data && !customer) setCustomer(preselected.data);
  }, [preselected.data, customer]);
  useEffect(() => {
    if (request.data && !title) setTitle(request.data.shortDescription);
  }, [request.data, title]);

  const create = useMutation({
    mutationFn: () =>
      apiPost<{ data: OfferResponse }>("/offers", {
        customerId: customer!.id,
        ...(requestId ? { requestId } : {}),
        title: title.trim(),
      }),
    onSuccess: (response) => navigate(`/offers/${response.data.id}`),
  });
  return (
    <div className="mx-auto max-w-xl">
      <PageHeader title={t("offer.new")} subtitle={t("offer.newSubtitle")} />
      <form
        className="space-y-4 rounded-xl border border-app-border bg-app-surface p-5"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        {request.data ? (
          <Notice>
            {t("offer.fromRequest", { number: request.data.number })}
          </Notice>
        ) : null}
        <Field label={t("common.customer")} required htmlFor="offer-customer">
          <CustomerPicker
            id="offer-customer"
            value={customer}
            onChange={setCustomer}
            disabled={Boolean(requestId)}
          />
        </Field>
        <Field label={t("common.title")} required htmlFor="offer-title">
          <InputText
            id="offer-title"
            value={title}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setTitle(event.target.value)
            }
            placeholder={t("offer.titlePlaceholder")}
          />
        </Field>
        <MutationError error={create.error} />
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="text"
            severity="secondary"
            onClick={() => navigate(-1)}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            loading={create.isPending || undefined}
            disabled={!customer || !title.trim()}
          >
            {t("offer.createDraft")}
          </Button>
        </div>
      </form>
    </div>
  );
}
